<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import { variantsApi } from '../stores/api.js';
  import { sanitizeRichText } from '../lib/sanitizeHtml.js';
  import { _ } from '../lib/i18n.js';
  import { Package, Loader, Star, Search, Clock } from 'lucide-svelte';

  let variants = [];
  let loading = true;
  let error = '';
  let search = '';
  let category = '';

  onMount(() => {
    loadCatalog();
  });

  async function loadCatalog() {
    loading = true;
    error = '';
    try {
      // The backend forces is_public=true, is_active=true and scopes prices
      // to the session's own customer for a customer-scoped session — this
      // page never sends customerId, active or public itself.
      variants = await variantsApi.getAll({ active: true }) || [];
      console.log('[Catalogo] Loaded variants:', variants.length);
    } catch (err) {
      console.error('[Catalogo] Load error:', err);
      error = err.message;
    } finally {
      loading = false;
    }
  }

  // Filtering is done here rather than by reloading: the catalogue a single
  // customer can see is small, and typing in the box should not queue a
  // request per keystroke.
  $: filteredVariants = variants.filter((variant) => {
    if (category && variant.product?.category !== category) return false;
    if (!search.trim()) return true;
    const needle = search.trim().toLowerCase();
    return [variant.code, variant.name, variant.product?.name]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(needle));
  });

  // The categories actually present, so the filter never offers an empty one.
  $: availableCategories = [...new Set(variants.map((variant) => variant.product?.category).filter(Boolean))];

  // Group by product for display, same shape as ProposalModal's variant picker.
  $: groupedVariants = filteredVariants.reduce((groups, variant) => {
    const productName = variant.product?.name || '-';
    if (!groups[productName]) {
      groups[productName] = [];
    }
    groups[productName].push(variant);
    return groups;
  }, {});

  function hasCustomerPrice(variant) {
    return variant.customerPrice !== null && variant.customerPrice !== undefined;
  }

  function effectivePrice(variant) {
    return hasCustomerPrice(variant) ? variant.customerPrice : variant.price;
  }

  /**
   * The hourly surcharges the backend resolved for this variant.
   *
   * Already ordered weekend, holiday, urgent, night, already floored at the
   * base price and already scoped to this customer's own negotiated rates —
   * this page only renders them.
   *
   * @param {object} variant
   * @returns {Array<{conditionCode: string, price: number, source: string}>}
   */
  function surcharges(variant) {
    return Array.isArray(variant.conditionPrices) ? variant.conditionPrices : [];
  }

  function formatPrice(price) {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (price === null || price === undefined || isNaN(numPrice)) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(numPrice);
  }
</script>

<Layout>
  <div class="space-y-6">
    <div class="flex items-center space-x-4">
      <div class="p-3 bg-linear-to-br from-primary-500 to-primary-600 rounded-2xl shadow-lg">
        <Package size={28} class="text-white" />
      </div>
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{$_('catalog.title')}</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400">{$_('catalog.subtitle')}</p>
      </div>
    </div>

    {#if loading}
      <div class="card p-12 text-center">
        <Loader size={48} class="mx-auto text-primary-500 animate-spin mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
      </div>
    {:else if error}
      <div class="card p-12 text-center">
        <p class="text-red-500">{error}</p>
      </div>
    {:else if variants.length === 0}
      <div class="card p-12 text-center">
        <Package size={48} class="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <p class="text-gray-500 dark:text-gray-400 text-lg">{$_('catalog.empty')}</p>
      </div>
    {:else}
      <div class="card p-4 flex flex-col sm:flex-row gap-3">
        <div class="relative flex-1">
          <Search size={16} class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            class="input pl-9"
            bind:value={search}
            placeholder={$_('catalog.searchPlaceholder')}
            aria-label={$_('catalog.searchPlaceholder')}
          />
        </div>
        {#if availableCategories.length > 1}
          <select class="input sm:w-56" bind:value={category} aria-label={$_('catalog.category')}>
            <option value="">{$_('products.categories.all')}</option>
            {#each availableCategories as availableCategory (availableCategory)}
              <option value={availableCategory}>{$_(`products.categories.${availableCategory}`)}</option>
            {/each}
          </select>
        {/if}
      </div>

      {#if filteredVariants.length === 0}
        <div class="card p-12 text-center">
          <Search size={48} class="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p class="text-gray-500 dark:text-gray-400 text-lg">{$_('catalog.noResults')}</p>
        </div>
      {:else}
        <div class="space-y-6">
          {#each Object.entries(groupedVariants) as [productName, productVariants] (productName)}
            <div class="card overflow-hidden">
              <div class="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <h2 class="text-lg font-semibold text-gray-900 dark:text-white">{productName}</h2>
              </div>
              <div class="divide-y divide-gray-200 dark:divide-gray-700">
                {#each productVariants as variant (variant.id)}
                  <div class="px-6 py-4">
                    <div class="flex items-start justify-between gap-4">
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2">
                          <span class="text-xs font-mono text-gray-400">{variant.code}</span>
                          <span class="font-medium text-gray-900 dark:text-white">{variant.name}</span>
                        </div>
                        {#if variant.description}
                          <div class="mt-1 text-sm text-gray-500 dark:text-gray-400 prose-sm max-w-none">
                            {@html sanitizeRichText(variant.description)}
                          </div>
                        {/if}
                        <div class="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-400">
                          <!-- Category lives on the variant (pv.category), not the
                               product, so it is shown per row: two variants of the
                               same product can differ. -->
                          {#if variant.product?.category}
                            <span class="font-bold uppercase tracking-wider px-2 py-0.5 border border-primary-500 text-primary-700 dark:text-primary-400">
                              {$_(`products.categories.${variant.product.category}`)}
                            </span>
                          {/if}
                          <span>{variant.unit}</span>
                          <span>{$_(`products.billingCycles.${variant.billingCycle}`)}</span>
                        </div>
                      </div>
                      <div class="text-right flex-shrink-0">
                        <p class="text-lg font-bold text-primary-600 dark:text-primary-400">
                          {formatPrice(effectivePrice(variant))}
                        </p>
                        {#if hasCustomerPrice(variant)}
                          <p class="text-xs text-emerald-700 dark:text-emerald-400 flex items-center justify-end gap-1">
                            <Star size={10} />
                            {$_('catalog.negotiatedPrice')}
                          </p>
                        {/if}
                      </div>
                    </div>

                    {#if surcharges(variant).length > 0}
                      <!--
                        Absolute hourly prices, not percentages, so each chip
                        carries the figure. The caption says so explicitly: a
                        list of condition names beside a base price reads as a
                        multiplier to anybody who has seen one elsewhere.
                      -->
                      <div class="mt-3 pt-3 border-t" style="border-color: var(--color-border-muted);">
                        <div class="flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style="color: var(--color-text-body);">
                          <Clock size={14} />
                          {$_('catalog.surcharges')}
                        </div>
                        <div class="mt-2 flex flex-wrap gap-2">
                          {#each surcharges(variant) as surcharge (surcharge.conditionCode)}
                            <span
                              class="inline-flex items-center gap-2 px-2 py-1 text-xs border-2"
                              style="border-color: {surcharge.source === 'customer' ? '#10b981' : 'var(--color-border-default)'};"
                            >
                              <span class="uppercase tracking-wide" style="color: var(--color-text-body);">
                                {$_(`products.variants.conditionPrices.${surcharge.conditionCode}`)}
                              </span>
                              <span class="font-bold" style="color: var(--color-text-heading);">
                                {formatPrice(surcharge.price)}
                              </span>
                              {#if surcharge.source === 'customer'}
                                <Star size={10} class="text-emerald-700 dark:text-emerald-400" />
                              {/if}
                            </span>
                          {/each}
                        </div>
                        <p class="mt-2 text-xs" style="color: var(--color-text-body);">{$_('catalog.surchargesHint')}</p>
                      </div>
                    {/if}
                  </div>
                {/each}
              </div>
            </div>
          {/each}
        </div>
      {/if}
    {/if}
  </div>
</Layout>
